import { type IApiError, type IApiV3Error } from '@webapp/services/api'

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Collapses per-record path segments (numeric ids, uuids) so that every
 * request to the same endpoint produces the same fingerprint.
 */
const normalizePath = (url: string) =>
  url
    .split('?')[0]
    .split('/')
    .map((segment) =>
      /^\d+$/.test(segment) || UUID_SEGMENT.test(segment) ? ':id' : segment
    )
    .join('/')

/**
 * The error thrown by the legacy `v1` / `v3` / `genericApi` axios clients.
 *
 * These interceptors used to reject with the bare `error.response` object. A
 * rejection with a non-Error value reaches Sentry as "Non-Error promise
 * rejection captured with keys: config, data, headers, message, request,
 * status, statusText" with no stack trace, which collapsed every unhandled
 * legacy API failure in the app into a single untriageable issue.
 *
 * The own properties mirror `AxiosResponse` so that existing `catch` blocks
 * reading `error.status` / `error.data.errors` keep working unchanged. The
 * failing request is appended to `message` (the stack is captured here in the
 * interceptor, so it is identical for every caller — without the request line
 * the event would not say what actually failed), and `sentryFingerprint`
 * groups unhandled rejections by endpoint + status instead of by that shared
 * stack (see `beforeSend` in `index.tsx`).
 */
export class ApiResponseError<T = any> extends Error implements IApiError<T> {
  name = 'ApiResponseError'

  data: IApiV3Error<T>
  status: number
  statusText: string
  headers: IApiError<T>['headers']
  declare config: IApiError<T>['config']
  declare request?: any
  sentryFingerprint?: string[]

  constructor(response: IApiError<T>, message: string) {
    const method = (response.config?.method || 'GET').toUpperCase()
    const url = response.config?.url

    super(url ? `${message} (${method} ${url})` : message)

    this.data = response.data
    this.status = response.status
    this.statusText = response.statusText
    this.headers = response.headers

    // The outbound request can carry credentials (e.g. a failed password
    // change holds the passwords in config.data), so keep both off Sentry
    // events — ExtraErrorData only serializes enumerable own properties.
    Object.defineProperties(this, {
      config: { value: response.config, writable: true },
      request: { value: response.request, writable: true },
    })

    if (url) {
      this.sentryFingerprint = [
        'ApiResponseError',
        method,
        normalizePath(url),
        String(response.status),
      ]
    }
  }
}
